import { useCallback, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { setUser, setLoading } from '../features/auth/authSlice';
import { useAppDispatch } from './hooks';
import type { SupplyPassportUser } from '../types/types';
import { clearSelectedOrganisation } from '../features/organisation/organisationSlice';
import { useNavigate } from 'react-router-dom';

export const useAuth = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(setLoading(true));

    const enrichUserWithProfile = async (user: SupplyPassportUser | null) => {
      if (!user) {
        dispatch(setUser(null));
        return;
      }

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching profile:', error);
        dispatch(setUser({ ...user, profile: undefined } as SupplyPassportUser));
        return;
      }

      dispatch(setUser({ ...user, profile } as SupplyPassportUser));
    };

    // Initial session load
    supabase.auth.getSession().then(({ data }) => {
      enrichUserWithProfile(data.session?.user ?? null);
    });

    // Listen for session changes
    const { data: listener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'PASSWORD_RECOVERY') {
          return; // Don't enrich or redirect, ResetPassword component handles it
        }
        enrichUserWithProfile(session?.user ?? null);
      }
    );

    return () => listener.subscription.unsubscribe();
  }, [dispatch]);

  const logout = useCallback(async () => {
    if (!supabase.auth.getSession) return; // sanity check
    const { error } = await supabase.auth.signOut({ scope: 'global' });
    if (
      error &&
      error.message !== 'Session from session_id claim in JWT does not exist'
    ) {
      console.error('Error signing out:', error);
    }
    dispatch(setUser(null));
    dispatch(clearSelectedOrganisation());
    navigate('/');
  }, [dispatch, navigate]);

  return { logout };
};
